<template>
    <div>
        <nav class="navbar navbar-expand-md navbar-dark bg-primary sticky-top">
            <div class="container-fluid">
                <ul class="navbar-nav ml-auto">
                    <li class="nav-item">
                        <a class="nav-link" href="/admin">
                            <i class="text-red-light fas fa-sign-in-alt"></i>Admin
                        </a>
                    </li>
                </ul>
            </div>
        </nav>
        <main>
            <div class="container-fluid">
                <div class="row full-screen-height">
                    <div class="col-lg-4 col-md-6 p-4">
                        <br />
                        <br />
                        <h2 class="t-800 text-secondary">Canon</h2>
                        <p>
                            Enviamos um código de 6 dígitos para o seu e-mail.
                            Digite-o abaixo para concluir o acesso.
                        </p>
                        <transition name="error">
                            <div v-show="error" class="two-factor-error">
                                <div class="alert alert-danger mb-3">
                                    <i class="fas fa-exclamation-triangle"></i> {{ errorMessage }}
                                </div>
                            </div>
                        </transition>
                        <form method="POST" @submit.prevent="verify" aria-label="TwoFactor">
                            <div class="form-group row">
                                <div class="col-md-12">
                                    <div class="two-factor-inputs" v-bind:class="{ 'two-factor-inputs--has-error': error }">
                                        <input
                                            v-for="(digit, index) in digits"
                                            :key="index"
                                            :ref="'digit' + index"
                                            type="text"
                                            inputmode="numeric"
                                            maxlength="1"
                                            class="form-control two-factor-digit"
                                            v-model="digits[index]"
                                            @input="onDigitInput(index)"
                                            @keydown.backspace="onBackspace(index, $event)"
                                            @paste.prevent="onPaste"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="form-group row">
                                <div class="col-md-12">
                                    <a
                                        href="#"
                                        @click.prevent="canResend && resend()"
                                        :class="{ 'two-factor-resend-disabled': !canResend }"
                                        class="two-factor-resend"
                                    >
                                        Re-enviar o e-mail com o código
                                        <span v-if="!canResend" class="two-factor-resend-timer">
                                            ({{ resendCountdownLabel }})
                                        </span>
                                    </a>
                                </div>
                            </div>
                            <div class="form-group row mb-0">
                                <div class="col-md-12 two-factor-actions">
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary two-factor-back"
                                        @click="goBack"
                                    >
                                        Voltar
                                    </button>
                                    <button type="submit" class="btn btn-primary" :disabled="loading">
                                        Autenticar
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div class="col-lg-8 col-md-6 bg-home"></div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { navigateAfterLogin } from "@/router/authNavigation";

const RESEND_COOLDOWN_SECONDS = 90;

export default {
    name: "TwoFactor",
    data() {
        return {
            digits: ["", "", "", "", "", ""],
            error: false,
            errorMessage: "Código inválido. Verifique e tente novamente.",
            loading: false,
            resendSecondsRemaining: RESEND_COOLDOWN_SECONDS,
            resendTimerId: null
        };
    },
    computed: {
        ...mapState("auth", ["pending2fa"]),
        canResend() {
            return this.resendSecondsRemaining === 0;
        },
        resendCountdownLabel() {
            const minutes = Math.floor(this.resendSecondsRemaining / 60);
            const seconds = this.resendSecondsRemaining % 60;
            return minutes + ":" + String(seconds).padStart(2, "0");
        }
    },
    mounted() {
        if (!this.pending2fa || !this.pending2fa.challenge_token) {
            this.$router.push({ name: "home" });
            return;
        }
        this.startResendCooldown();
        this.focusDigit(0);
    },
    beforeDestroy() {
        this.clearResendCooldown();
    },
    methods: {
        startResendCooldown() {
            this.clearResendCooldown();
            this.resendSecondsRemaining = RESEND_COOLDOWN_SECONDS;
            this.resendTimerId = setInterval(() => {
                if (this.resendSecondsRemaining > 0) {
                    this.resendSecondsRemaining--;
                } else {
                    this.clearResendCooldown();
                }
            }, 1000);
        },
        clearResendCooldown() {
            if (this.resendTimerId) {
                clearInterval(this.resendTimerId);
                this.resendTimerId = null;
            }
        },
        clearError() {
            this.error = false;
        },
        focusDigit(index) {
            this.$nextTick(() => {
                const ref = this.$refs["digit" + index];
                if (ref && ref[0]) {
                    ref[0].focus();
                }
            });
        },
        onDigitInput(index) {
            this.clearError();
            const value = this.digits[index].replace(/\D/g, "");
            this.digits.splice(index, 1, value.slice(-1));

            if (value && index < this.digits.length - 1) {
                this.focusDigit(index + 1);
            }
        },
        onBackspace(index, event) {
            if (!this.digits[index] && index > 0) {
                event.preventDefault();
                this.focusDigit(index - 1);
            }
        },
        onPaste(event) {
            this.clearError();
            const pasted = (event.clipboardData || window.clipboardData)
                .getData("text")
                .replace(/\D/g, "")
                .slice(0, 6);

            for (let i = 0; i < this.digits.length; i++) {
                this.digits.splice(i, 1, pasted[i] || "");
            }

            this.focusDigit(Math.min(pasted.length, this.digits.length - 1));
        },
        showError(message, autoHide) {
            this.error = true;
            this.errorMessage = message;

            if (autoHide) {
                setTimeout(() => {
                    this.error = false;
                }, 4000);
            }
        },
        verify() {
            const code = this.digits.join("");

            if (code.length !== 6) {
                this.showError("Informe o código completo de 6 dígitos.", true);
                return;
            }

            this.loading = true;

            this.$store
                .dispatch("auth/auth_verify_2fa", {
                    challenge_token: this.pending2fa.challenge_token,
                    code,
                    email: this.pending2fa.email,
                    password: this.pending2fa.password
                })
                .then(() => {
                    return navigateAfterLogin(this.$router, this.$store.getters["auth/getSessionRoles"]);
                })
                .catch(err => {
                    const message =
                        (err.response &&
                            err.response.data &&
                            err.response.data.message) ||
                        "";

                    if (message.toLowerCase().indexOf("expired") !== -1) {
                        this.showError("Código expirado. Solicite um novo e-mail.", false);
                    } else if (message.toLowerCase().indexOf("too many") !== -1) {
                        this.showError("Número máximo de tentativas atingido. Solicite um novo e-mail.", false);
                    } else {
                        this.showError("Código inválido. Verifique e tente novamente.", false);
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        resend() {
            if (!this.canResend) {
                return;
            }

            this.$store
                .dispatch("auth/auth_resend_2fa", {
                    challenge_token: this.pending2fa.challenge_token
                })
                .then(() => {
                    this.digits = ["", "", "", "", "", ""];
                    this.clearError();
                    this.startResendCooldown();
                    this.focusDigit(0);
                })
                .catch(() => {
                    this.showError("Não foi possível reenviar o código.", false);
                });
        },
        goBack() {
            this.$store.dispatch("auth/auth_cancel_2fa");
            this.$router.push({ name: "home" });
        }
    }
};
</script>

<style scoped>
.two-factor-inputs {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-start;
}

.two-factor-digit {
    width: 3rem;
    height: 3rem;
    text-align: center;
    font-size: 1.5rem;
    font-weight: 700;
    padding: 0;
}

.two-factor-resend {
    font-size: 0.95rem;
}

.two-factor-resend-disabled {
    pointer-events: none;
    color: #999;
    cursor: not-allowed;
}

.two-factor-resend-timer {
    margin-left: 0.25rem;
}

.two-factor-actions {
    display: flex;
    gap: 0.75rem;
}

.two-factor-back {
    color: #000;
    border-color: #000;
}

.two-factor-back:hover {
    color: #000;
    background-color: #f8f9fa;
}

.two-factor-inputs--has-error .two-factor-digit {
    border: 1px solid #cc0000 !important;
}

.error-enter-active,
.error-leave-active {
    transition: opacity 0.75s;
}

.error-enter,
.error-leave-to {
    opacity: 0;
}
</style>
